<template>
    <view-container class="dca-view-plan">
        <template #header>
            <view-header back-button @back="$emit(EmitEvents.BACK)" />
        </template>

        <h1>{{ t('widget.dollar-cost-average.view-plan.title', { currency: getCryptoCurrencyName(currentDca.cryptoCurrency) }) }}</h1>
        <p class="text center">{{ t('widget.dollar-cost-average.view-plan.description') }}</p>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.amount') }}</label>
            <span v-if="!!dcaRequest.fiatAmount">{{ formatFiat(dcaRequest.fiatAmount) }}</span>
            <span v-else>
                {{ t('widget.dollar-cost-average.view-plan.amount.min-max', { minimum: formatFiat(minimum || 30), maximum: formatFiat(maximum) }) }}
            </span>
        </p>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.bank-name') }}</label>
            <span>
                {{ currentDca.bankName }}
                <copy-to-clipboard :value="currentDca.bankName" />
            </span>
        </p>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.iban') }}</label>
            <span>
                {{ currentDca.bankIban }}
                <copy-to-clipboard :value="currentDca.bankIban" />
            </span>
        </p>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.bic') }}</label>
            <span>
                {{ currentDca.bankBic }}
                <copy-to-clipboard :value="currentDca.bankBic" />
            </span>
        </p>

        <p class="dca-plan-value" v-if="!!dcaRequest.recurrence">
            <label>{{ t('widget.dollar-cost-average.view-plan.recurrence') }}</label>
            {{ dcaRequest.recurrence }}
        </p>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.dca-code') }}</label>
            <span>
                {{ currentDca.code }}
                <copy-to-clipboard :value="currentDca.code" />
            </span>
        </p>

        <notification type="info">
            {{ t('widget.dollar-cost-average.view-plan.notification', { currency: getCryptoCurrencyName(currentDca.cryptoCurrency) }) }}
        </notification>

        <p class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.wallet-address') }}</label>
            <span class="wallet-address">{{ currentDca.walletAddress }}</span>
        </p>

        <p v-if="currentDca.cryptoCurrency === 'XRP' && !!currentDca.destinationTag" class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.destination-tag') }}</label>
            <span class="wallet-address">{{ currentDca.destinationTag }}</span>
        </p>

        <p v-if="currentDca.apiClientName" class="dca-plan-value">
            <label>{{ t('widget.dollar-cost-average.view-plan.api-client') }}</label>
            {{ currentDca.apiClientName }}
        </p>

        <template #footer>
            <call-to-action class="secondary" @clicked="emit(EmitEvents.COMPLETED)">
                {{ t('widget.dollar-cost-average.view-plan.button.back') }}
            </call-to-action>
        </template>
    </view-container>
</template>

<script lang="ts" setup>
import { useDca } from '@/dollar-cost-average/composables/dca';
import getCryptoCurrencyName from '@/dollar-cost-average/functions/currencies';
import { CallToAction, Notification, ViewContainer, ViewHeader } from '@btcdirect/components';
import { useNumberFormat, useTranslations } from '@btcdirect/composables';
import CopyToClipboard from '@btcdirect/shared/src/components/copy-to-clipboard.vue';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { CurrencyPair, useCurrenciesStore } from '@btcdirect/stores/src/currencies'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { usePaymentMethodsStore } from '@btcdirect/stores/src/payment-methods'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { computed } from 'vue';

const emit = defineEmits([EmitEvents.BACK, EmitEvents.COMPLETED]);

const currenciesStore = useCurrenciesStore();

const { dcaRequest, currentDca } = useDca();
const { formatFiat } = useNumberFormat();
const { t } = useTranslations();

const selectedCurrency = computed<CurrencyPair>(() => currenciesStore.getCurrencyPair(currentDca.cryptoCurrency));
const minimum = computed<number>(() => selectedCurrency.value.buy.min.amount ?? null);

const paymentMethodsStore = usePaymentMethodsStore();
const maximum = computed<number>(() => paymentMethodsStore.bankTransferLimit);
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/fonts';

.dca-view-plan {
    .dca-plan-value {
        @include flex.flex(flex, column);

        label {
            @include fonts.gt-walsheim-medium;
            display: block;
        }

        span {
            @include flex.flex;
            @include flex.align($vertical: space-between);

            &.wallet-address {
                word-break: break-all;
            }
        }
    }
}
</style>
